import {
  Box,
  Button,
  Stack,
  Chip,
  TextField,
  Autocomplete,
} from '@mui/material';
import { useNavigate } from 'react-router';
import { DatePicker } from '@mui/x-date-pickers';
import dayjs, { Dayjs } from 'dayjs'; // Import Dayjs
import { HOME_PAGE } from '../../common/constants/routes.constants';
import Breadcrumbs from '@mui/material/Breadcrumbs';
import Typography from '@mui/material/Typography';
import React, { useState } from 'react';
import BackButton from '../../components/BackButton/BackButton';
import { createToastNotification } from '../../utilities/helpers';
import { createFair } from '../../api/fairs';

const CreateFair = () => {
  const navigate = useNavigate();
  const [fairData, setFairData] = useState({
    name: '',
    description: '',
    startDate: '', // Use string for date
    endDate: '',
    category: [] as string[],
    categoryInput: '',
  });

  const navigateToHome = () => {
    navigate(HOME_PAGE);
  };

  const handleAddChip = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter' && fairData.categoryInput.trim() !== '') {
      setFairData((prev) => ({
        ...prev,
        category: [...prev.category, prev.categoryInput.trim()],
        categoryInput: '',
      }));
    }
  };

  const handleDeleteChip = (index: number) => {
    setFairData((prev) => ({
      ...prev,
      category: prev.category.filter((_, i) => i !== index),
    }));
  };

  const handleChange = (field: string, value: any) => {
    setFairData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleDateChange = (
    field: 'startDate' | 'endDate',
    date: Dayjs | null
  ) => {
    const formattedDate = date ? date.format('YYYY-MM-DD') : '';
    setFairData((prev) => ({
      ...prev,
      [field]: formattedDate,
    }));
  };

  const handleSubmit = async () => {
    const { categoryInput, ...dataToSubmit } = fairData; // Exclude `categoryInput` from submission
    if(!fairData.name.trim()) {
      createToastNotification('info', 'Naziv je obavezno polje');
      return;
    }
    try {
      await createFair(dataToSubmit);
      createToastNotification('success', 'Sajam je uspešno sačuvan');
      navigate('/');
    } catch (error: any) {
      createToastNotification('error', error?.response?.data?.message);
    }
  };

  return (
    <Box>
      <BackButton title={'SAJMOVI'} backNavigate={navigateToHome} />
      <Breadcrumbs aria-label="breadcrumb" sx={{ ml: 4, mt: 1 }}>
        <Typography sx={{ color: 'text.primary' }}>Kreiraj sajam </Typography>
        <Typography variant="body2">Kreiraj halu </Typography>
        <Typography>Objavi </Typography>
      </Breadcrumbs>
      <Stack p={4} spacing={4} mt={4} ml={4} mr={4}>
        <TextField
          fullWidth
          label="Naziv"
          value={fairData.name}
          onChange={(e) => handleChange('name', e.target.value)}
          required
          InputLabelProps={{ required: true }}
        />
        <TextField
          fullWidth
          label="Opis"
          multiline
          rows={6}
          value={fairData.description}
          onChange={(e) => handleChange('description', e.target.value)}
        />
        <Stack direction="row" gap={3}>
          <DatePicker
            label={'Datum početka'}
            sx={{ flex: 1 }}
            value={fairData.startDate ? dayjs(fairData.startDate) : null}
            onChange={(date) => handleDateChange('startDate', date)}
          />
          <DatePicker
            label={'Datum završetka'}
            sx={{ flex: 1 }}
            value={fairData.endDate ? dayjs(fairData.endDate) : null}
            onChange={(date) => handleDateChange('endDate', date)}
          />
        </Stack>
        <Autocomplete
          multiple
          limitTags={5}
          options={[]}
          value={fairData.category}
          noOptionsText=""
          onChange={(event, newValue) => {
            setFairData((prev) => ({
              ...prev,
              category: newValue as string[],
            }));
          }}
          renderTags={(value, getTagProps) =>
            value.map((option, index) => {
              const tagProps = getTagProps({ index });
              return (
                <Chip
                  label={option}
                  {...tagProps}
                  color="primary"
                  size="small"
                  onDelete={() => handleDeleteChip(index)}
                  sx={{
                    maxWidth: 150,
                    overflow: 'hidden',
                    whiteSpace: 'nowrap',
                    textOverflow: 'ellipsis',
                  }}
                />
              );
            })
          }
          renderInput={(params) => (
            <TextField
              {...params}
              label="Kategorija"
              variant="outlined"
              fullWidth
              value={fairData.categoryInput}
              onChange={(e) =>
                setFairData((prev) => ({
                  ...prev,
                  categoryInput: e.target.value,
                }))
              }
              onKeyDown={handleAddChip}
            />
          )}
        />
        <Stack direction="row" justifyContent="flex-start">
          <Button variant="contained" onClick={handleSubmit}>
            Kreiraj novi sajam
          </Button>
        </Stack>
      </Stack>
    </Box>
  );
};

export default CreateFair;
